import { TextField, TextFieldProps, VirtualKeyboardPopperEnable } from '@nexelem/components';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { MaxWidthProp, maxWidthVariants } from '../utils/maxWidthVariants';
import { cn } from '@nexelem/utils';

type InternalFormInputProps<Value extends FieldValues> = {
  name: Path<Value>;
  control?: Control<Value>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  maxWidth?: MaxWidthProp;
  showErrorMessage?: boolean;
  enableClearButton?: boolean;
  enableVirtualKeyboard?: VirtualKeyboardPopperEnable;
};

type FormInputProps<Value extends FieldValues> = Omit<
  TextFieldProps<string>,
  keyof InternalFormInputProps<Value> | 'errorMessage'
> &
  InternalFormInputProps<Value>;

export const FormTextInput = <Value extends FieldValues>({
  label,
  placeholder,
  maxWidth = 560,
  showErrorMessage = true,
  helperText,
  enableClearButton,
  className,
  disabled,
  ...props
}: FormInputProps<Value>) => {
  const { field, fieldState } = useController(props);

  const errorMessage = showErrorMessage ? fieldState.error?.message : undefined;
  const hasError = !!fieldState.error;

  const value = field.value === undefined ? '' : field.value;

  return (
    <TextField
      {...field}
      {...props}
      className={cn(maxWidthVariants({ maxWidth }), className)}
      disabled={disabled}
      enableClearButton={enableClearButton}
      errorMessage={errorMessage}
      helperText={helperText}
      label={label}
      placeholder={placeholder}
      showErrorBorder={hasError}
      value={value}
    />
  );
};
